import { useMemo, useState } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  CheckCircle2,
  ListChecks,
  Plus,
  Briefcase,
  User as UserIcon,
  Calendar,
  Loader2,
  AlertCircle,
  CalendarDays,
  Archive,
  Flame,
  Inbox,
  Target,
  Clock,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

type Scope = "mine" | "overdue" | "today" | "week" | "all";

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDueDate(d: Date | string | null | undefined) {
  if (!d) return null;
  const date = typeof d === "string" ? new Date(d) : d;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 0 && min > -60) return { text: `${-min}min atrás`, overdue: true };
  if (min < -60 && min > -24 * 60)
    return { text: `${Math.round(-min / 60)}h atrás`, overdue: true };
  if (min < -24 * 60) {
    const d2 = Math.round(-min / (24 * 60));
    return { text: `${d2}d atrás`, overdue: true };
  }
  if (min < 60) return { text: `em ${min}min`, overdue: false };
  if (min < 24 * 60) return { text: `em ${Math.round(min / 60)}h`, overdue: false };
  const d2 = Math.round(min / (24 * 60));
  if (d2 < 7) return { text: `em ${d2}d`, overdue: false };
  return { text: date.toLocaleDateString("pt-BR"), overdue: false };
}

const PRIORITY_CFG: Record<string, { label: string; color: string; bar: string; icon: any }> = {
  high: {
    label: "Alta",
    color: "text-red-700 dark:text-red-300",
    bar: "bg-red-500",
    icon: Flame,
  },
  medium: {
    label: "Média",
    color: "text-amber-700 dark:text-amber-300",
    bar: "bg-amber-500",
    icon: AlertCircle,
  },
  low: {
    label: "Baixa",
    color: "text-slate-600 dark:text-slate-400",
    bar: "bg-slate-400",
    icon: Target,
  },
};

// ─── New Task Sheet ──────────────────────────────────────────────────────────
function NewTaskSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const utils = trpc.useUtils();
  const create = trpc.crm.tasks.create.useMutation({
    onSuccess: () => {
      toast.success("Tarefa criada");
      onOpenChange(false);
      setTitle("");
      setDescription("");
      setDueAt("");
      setPriority("medium");
      utils.crm.tasks.list.invalidate();
      utils.crm.tasks.bucketCounts.invalidate();
      onCreated?.();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar tarefa")),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[420px] w-full p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <ListChecks className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>Nova tarefa</SheetTitle>
              <SheetDescription>Crie um lembrete de ação comercial</SheetDescription>
            </div>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <Label className="text-xs">Título</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              placeholder="Ex: Ligar para decisor sobre proposta"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Descrição (opcional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1"
              placeholder="Contexto, o que falar, pontos de atenção..."
            />
          </div>
          <div>
            <Label className="text-xs">Prazo</Label>
            <div className="relative mt-1">
              <Calendar className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
              <Input
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Prioridade</Label>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {(["low", "medium", "high"] as const).map((p) => {
                const cfg = PRIORITY_CFG[p];
                const Icon = cfg.icon;
                const active = priority === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`flex items-center justify-center gap-1.5 rounded-md border px-2.5 py-2 text-sm transition ${
                      active ? "border-primary bg-primary/5" : "hover:bg-muted/40"
                    }`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
                    <span>{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="border-t px-6 py-4 flex items-center justify-end gap-2 bg-muted/30">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!title.trim()) return toast.error("Título obrigatório");
              create.mutate({
                title: title.trim(),
                description: description || null,
                dueAt: dueAt ? new Date(dueAt) : null,
                priority,
              });
            }}
            disabled={create.isPending}
          >
            {create.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Criar tarefa
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Task Row ────────────────────────────────────────────────────────────────
function TaskRow({
  row,
  onComplete,
  onDelete,
}: {
  row: any;
  onComplete: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  const task = row.task;
  const isDone = task.status === "done";
  const priority = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.medium;
  const due = formatDueDate(task.dueAt);
  return (
    <div
      className={`group relative flex items-start gap-3 rounded-lg border bg-card px-3 py-3 pl-4 transition hover:shadow-sm ${
        isDone ? "opacity-60" : ""
      } ${due?.overdue && !isDone ? "border-red-300 dark:border-red-900" : ""}`}
    >
      {!isDone && (
        <div
          className={`absolute inset-y-0 left-0 w-1 rounded-l-lg ${priority.bar}`}
          aria-hidden
        />
      )}

      <button
        onClick={() => !isDone && onComplete(task.id)}
        className={`mt-0.5 h-5 w-5 rounded-full border-2 shrink-0 transition flex items-center justify-center ${
          isDone
            ? "bg-primary border-primary"
            : "hover:border-primary hover:bg-primary/10"
        }`}
        title={isDone ? "Concluída" : "Concluir"}
        disabled={isDone}
      >
        {isDone && <CheckCircle2 className="h-3.5 w-3.5 text-primary-foreground" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className={`text-sm font-medium ${isDone ? "line-through" : ""} flex-1`}>
            {task.title}
          </div>
          {!isDone && task.priority === "high" && (
            <Flame className="h-3.5 w-3.5 text-red-600 shrink-0" />
          )}
        </div>
        {task.description && !isDone && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {task.description}
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          {due && (
            <span
              className={`flex items-center gap-1 ${
                due.overdue && !isDone
                  ? "text-red-600 font-medium"
                  : "text-muted-foreground"
              }`}
            >
              <Calendar className="h-3 w-3" />
              {due.text}
            </span>
          )}
          {row.deal?.id && (
            <Link
              href={`/crm/deals/${row.deal.id}`}
              className="flex items-center gap-1 text-muted-foreground hover:text-primary"
            >
              <Briefcase className="h-3 w-3" />
              <span className="truncate max-w-[220px]">{row.deal.title}</span>
            </Link>
          )}
          {row.contact?.id && !row.deal?.id && (
            <Link
              href={`/contacts/${row.contact.id}`}
              className="flex items-center gap-1 text-muted-foreground hover:text-primary"
            >
              <UserIcon className="h-3 w-3" />
              {[row.contact.name, row.contact.lastName].filter(Boolean).join(" ")}
            </Link>
          )}
        </div>
      </div>

      {row.owner?.name && (
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
            {initials(row.owner.name)}
          </AvatarFallback>
        </Avatar>
      )}

      <Button
        size="icon"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100 shrink-0 h-8 w-8"
        onClick={() => onDelete(task.id)}
        title="Excluir"
      >
        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
      </Button>
    </div>
  );
}

// ─── Sidebar scope button ────────────────────────────────────────────────────
function ScopeButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
  tint = "default",
}: {
  active: boolean;
  onClick: () => void;
  icon: any;
  label: string;
  count: number | null;
  tint?: "default" | "red" | "primary" | "emerald";
}) {
  const tintMap = {
    default: "text-muted-foreground",
    red: "text-red-600",
    primary: "text-primary",
    emerald: "text-emerald-600",
  };
  return (
    <button
      onClick={onClick}
      className={`group w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition ${
        active
          ? "bg-primary/10 text-foreground font-medium"
          : "text-muted-foreground hover:bg-muted"
      }`}
    >
      <Icon className={`h-4 w-4 ${active ? "text-primary" : tintMap[tint]}`} />
      <span className="flex-1 text-left">{label}</span>
      {count !== null && count > 0 && (
        <Badge variant={active ? "default" : "secondary"} className="h-5 text-[10px]">
          {count}
        </Badge>
      )}
    </button>
  );
}

// ─── Body ────────────────────────────────────────────────────────────────────
function CrmTasksContent() {
  const [scope, setScope] = useState<Scope>("mine");
  const [newOpen, setNewOpen] = useState(false);
  const utils = trpc.useUtils();

  const tasksQuery = trpc.crm.tasks.list.useQuery({
    scope,
    status: scope === "all" ? undefined : "pending",
  });
  const bucketCounts = trpc.crm.tasks.bucketCounts.useQuery();

  const complete = trpc.crm.tasks.complete.useMutation({
    onSuccess: () => {
      utils.crm.tasks.list.invalidate();
      utils.crm.tasks.bucketCounts.invalidate();
    },
  });
  const del = trpc.crm.tasks.delete.useMutation({
    onSuccess: () => {
      utils.crm.tasks.list.invalidate();
      utils.crm.tasks.bucketCounts.invalidate();
    },
  });

  const tasks = tasksQuery.data ?? [];

  // As tarefas que estão na tela, com o vencimento — é o que permite
  // "conclua a de hoje" sem a pessoa dizer o id.
  useContextoDeLista({
    titulo: "Tarefas do CRM",
    total: tasks.length,
    itens: tasks.map((t: any) => ({
      id: t.task.id,
      titulo: t.task.title,
      status: t.task.status,
      prazo: t.task.dueAt,
      prioridade: t.task.priority,
      responsavel: t.owner?.name ?? null,
    })),
  });

  const grouped = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);

    const out = {
      overdue: [] as any[],
      today: [] as any[],
      upcoming: [] as any[],
      noDate: [] as any[],
      done: [] as any[],
    };
    for (const t of tasks) {
      if (t.task.status === "done") {
        out.done.push(t);
        continue;
      }
      if (!t.task.dueAt) {
        out.noDate.push(t);
        continue;
      }
      const due = new Date(t.task.dueAt);
      if (due < now && due < startOfToday) out.overdue.push(t);
      else if (due >= startOfToday && due < endOfToday) out.today.push(t);
      else if (due < now) out.overdue.push(t);
      else out.upcoming.push(t);
    }
    return out;
  }, [tasks]);

  const totalCount =
    grouped.overdue.length +
    grouped.today.length +
    grouped.upcoming.length +
    grouped.noDate.length +
    grouped.done.length;

  const scopeLabel: Record<Scope, string> = {
    mine: "Minhas tarefas",
    overdue: "Atrasadas",
    today: "Hoje",
    week: "Próximos 7 dias",
    all: "Todas",
  };

  return (
    <div className="space-y-5">
      {/* ── Page header ─────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ListChecks className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">Tarefas</h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Lembretes de ações comerciais
            </p>
          </div>
        </div>
        <Button onClick={() => setNewOpen(true)} className="shrink-0">
          <Plus className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Nova tarefa</span>
          <span className="sm:hidden ml-1.5">Nova</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
        {/* ── Sidebar filtros ───────────────────── */}
        <Card className="h-fit lg:sticky lg:top-4">
          <CardContent className="p-2 space-y-0.5">
            <ScopeButton
              active={scope === "mine"}
              onClick={() => setScope("mine")}
              icon={Inbox}
              label="Minhas"
              count={bucketCounts.data?.mine ?? null}
              tint="primary"
            />
            <ScopeButton
              active={scope === "overdue"}
              onClick={() => setScope("overdue")}
              icon={AlertCircle}
              label="Atrasadas"
              count={bucketCounts.data?.overdue ?? null}
              tint="red"
            />
            <ScopeButton
              active={scope === "today"}
              onClick={() => setScope("today")}
              icon={Calendar}
              label="Hoje"
              count={bucketCounts.data?.today ?? null}
              tint="primary"
            />
            <ScopeButton
              active={scope === "week"}
              onClick={() => setScope("week")}
              icon={CalendarDays}
              label="Esta semana"
              count={bucketCounts.data?.week ?? null}
            />
            <Separator className="my-1" />
            <ScopeButton
              active={scope === "all"}
              onClick={() => setScope("all")}
              icon={Archive}
              label="Todas"
              count={null}
            />
          </CardContent>
        </Card>

        {/* ── Lista ─────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{scopeLabel[scope]}</h2>
              <p className="text-xs text-muted-foreground">
                {totalCount} tarefa{totalCount === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          {tasksQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : totalCount === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <ListChecks className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <h3 className="font-semibold mb-1">Tudo em dia</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Nenhuma tarefa nesta visualização.
                </p>
                <Button size="sm" onClick={() => setNewOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Criar tarefa
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-5">
              {grouped.overdue.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <h3 className="text-sm font-semibold text-red-600">Atrasadas</h3>
                    <Badge variant="destructive" className="h-5 text-[10px]">
                      {grouped.overdue.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {grouped.overdue.map((row: any) => (
                      <TaskRow
                        key={row.task.id}
                        row={row}
                        onComplete={(id) => complete.mutate({ id })}
                        onDelete={(id) => del.mutate({ id })}
                      />
                    ))}
                  </div>
                </section>
              )}
              {grouped.today.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">Hoje</h3>
                    <Badge variant="secondary" className="h-5 text-[10px]">
                      {grouped.today.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {grouped.today.map((row: any) => (
                      <TaskRow
                        key={row.task.id}
                        row={row}
                        onComplete={(id) => complete.mutate({ id })}
                        onDelete={(id) => del.mutate({ id })}
                      />
                    ))}
                  </div>
                </section>
              )}
              {grouped.upcoming.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">Próximas</h3>
                    <Badge variant="secondary" className="h-5 text-[10px]">
                      {grouped.upcoming.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {grouped.upcoming.map((row: any) => (
                      <TaskRow
                        key={row.task.id}
                        row={row}
                        onComplete={(id) => complete.mutate({ id })}
                        onDelete={(id) => del.mutate({ id })}
                      />
                    ))}
                  </div>
                </section>
              )}
              {grouped.noDate.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">Sem prazo</h3>
                    <Badge variant="secondary" className="h-5 text-[10px]">
                      {grouped.noDate.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {grouped.noDate.map((row: any) => (
                      <TaskRow
                        key={row.task.id}
                        row={row}
                        onComplete={(id) => complete.mutate({ id })}
                        onDelete={(id) => del.mutate({ id })}
                      />
                    ))}
                  </div>
                </section>
              )}
              {grouped.done.length > 0 && (
                <details className="pt-2">
                  <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Concluídas ({grouped.done.length})
                  </summary>
                  <div className="mt-3 space-y-2">
                    {grouped.done.map((row: any) => (
                      <TaskRow
                        key={row.task.id}
                        row={row}
                        onComplete={(id) => complete.mutate({ id })}
                        onDelete={(id) => del.mutate({ id })}
                      />
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      <NewTaskSheet open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

export default function CrmTasks() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:tasks_view">
        <CrmTasksContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
